// Convierte un índice numérico en letra (ejemplo: 0 -> A, 1 -> B)
export const getLetter = (index) => String.fromCharCode(65 + index);

// Densidad del concreto en toneladas por metro cúbico
export const densityConcrete = 2.4; 

// Calcula el volumen y el peso de un elemento estructural
export const calculateVolumeAndWeight = (area, length) => 
{  
  const volume = parseFloat(Number((area / 10000) * (length)).toFixed(2)); // m³
  const weight = parseFloat(Number(volume * densityConcrete).toFixed(4)); // Toneladas
  return { volume, weight };
};

// Función para generar un valor aleatorio entre 2 y 6 metros, con pasos de 0.05 metros
const getRandomSpacing = () => 
{
  const min = 2;
  const max = 6;
  const step = 0.05;
  return parseFloat(Number(Math.round((Math.random() * (max - min) + min) / step) * step).toFixed(2));
};

const getRandomHeight = () => 
{
  const min = 2.3;
  const max = 2.6;
  const step = 0.05;
  return parseFloat(Math.round((Math.random() * (max - min) + min) / step) * step).toFixed(2);
};

// Función para actualizar la matriz de alturas
// Función para actualizar la matriz de alturas
export const createHeightMatrix = (floors, prevMatrix = []) => 
{
  // Crear una nueva matriz con solo las secciones necesarias
  const newMatrix = prevMatrix
    .filter((item) => {
      const pisoNum = parseInt(item.section.replace('piso', ''));
      return pisoNum <= floors;
    });

  // Crear un Set con las secciones que ya existen para evitar duplicados
  const existingSections = new Set(newMatrix.map((item) => item.section));     

  // Añadir nuevas secciones si floors ha aumentado
  for (let i = 0; i < floors; i++) 
  {
    const sectionKey = `piso${i+1}`;
    if (!existingSections.has(sectionKey)) 
    {
      let height = getRandomHeight();
      newMatrix.push({ section: sectionKey, height: parseFloat(height) });
    }
  } 

  return newMatrix;
};

// Función para actualizar la matriz sin perder los valores anteriores
export const createSpacingMatrix = (xAxis, zAxis, prevMatrix = []) => 
{
  const newMatrix = [...prevMatrix]; // Copia de la matriz anterior

  // Crear un Set con las secciones que ya existen para evitar duplicados
  const existingSections = new Set(prevMatrix.map((item) => item.section));

  // Generar matriz horizontal (letras en X)
  for (let i = 0; i < xAxis - 1; i++) 
  {
    let spacing = getRandomSpacing();
    const sectionKey = `${getLetter(i)}1 - ${getLetter(i + 1)}1`;
    if (!existingSections.has(sectionKey)) 
    {
      newMatrix.push(
        { 
          section: sectionKey, 
          spacing:  parseFloat(spacing),
          direction: 'x'
        });
    }
  }

  // Generar matriz vertical (números en Z)
  for (let j = 0; j < zAxis - 1; j++) 
  {
    let spacing = getRandomSpacing();
    const sectionKey = `${getLetter(0)}${j + 1} - ${getLetter(0)}${j + 2}`;
    if (!existingSections.has(sectionKey)) 
    {
      newMatrix.push(
        { 
          section: sectionKey, 
          spacing: spacing,
          direction: 'z'
        });
    }
  }

  return newMatrix;
};

export const createGridMatrix = (spacingMatrix, xAxis, zAxis) => 
{ 
  if (spacingMatrix.length === 0) return [];

  const newPoints = [];

  // Inicializar posiciones en X y Z
  const xSections = [0];
  const zSections = [0];  

  let prevX = 0;
  let prevZ = 0;
  spacingMatrix.forEach((item) => 
  {
    switch (item.direction) 
    {
      
      case "x":
        prevX = prevX + (item.spacing);
        xSections.push(parseFloat(prevX))
        break;
      case "z":
        prevZ = prevZ + parseFloat(item.spacing);
        zSections.push(parseFloat(prevZ))
        break;
      default:
        break;
    }    
  });

  // Crear puntos del grid
  for (let j = 0; j < zAxis; j++) 
  {
    for (let i = 0; i < xAxis; i++) 
    {
      if (isNaN(xSections[i]) || isNaN(zSections[j])) 
      {
        break; // Sale del bucle 'for' si se encuentra un NaN en zSections[j]
      }

      newPoints.push(
      {
        id: `${String.fromCharCode(65 + i)}${j + 1}`,
        x: xSections[i],
        z: zSections[j],
      });
    }
  }

  return newPoints;
};

export const calculateArea = (spacingMatrix, xAxis, zAxis, floors) => 
{ 
  if (spacingMatrix.length === 0) return [];

  const newPoints = [];

  // Inicializar posiciones en X y Z
  const xSections = [0];
  const zSections = [0];  

  let prevX = 0;
  let prevZ = 0;
  
  spacingMatrix.forEach((item) => 
  {
    switch (item.direction) 
    {
      
      case "x":
        prevX = prevX + (item.spacing);
        xSections.push(parseFloat(prevX))
        break;
      case "z":
        prevZ = prevZ + parseFloat(item.spacing);
        zSections.push(parseFloat(prevZ))
        break;
      default:
        break;
    }    
  });

  // Crear puntos del grid
  for (let j = 0; j < zAxis; j++) 
  {
    for (let i = 0; i < xAxis; i++) 
    {
      if (isNaN(xSections[i]) || isNaN(zSections[j])) 
      {
        break; // Sale del bucle 'for' si se encuentra un NaN en zSections[j]
      }

      newPoints.push(
      {
        id: `${String.fromCharCode(65 + i)}${j + 1}`,
        x: xSections[i],
        z: zSections[j],
      });
    }
  }
  
  const parcialArea = Number(xSections[xAxis-1] * zSections[zAxis-1]).toFixed(2);
  const totalArea = parcialArea * (floors);

  return totalArea;
};

export const createNodesMatrix = (spacingMatrix, xAxis, zAxis, heightMatrix) => 
{
  const nodes = [];

  // Inicializar posiciones en X, Z y Y
  const xPositions = [0];
  const zPositions = [0];
  const yPositions = [0]; // Siempre inicia desde 0

  // Acumular separaciones en X
  for (let i = 0; i < xAxis - 1; i++) 
  {
    const section = spacingMatrix.find(
      (s) => s.section === `${String.fromCharCode(65 + i)}1 - ${String.fromCharCode(65 + i + 1)}1`
    );
    xPositions.push(xPositions[i] + (section ? parseFloat(section.spacing) : 3));
  }

  // Acumular separaciones en Z
  for (let j = 0; j < zAxis - 1; j++) 
  {
    const section = spacingMatrix.find((s) => s.section === `A${j + 1} - A${j + 2}`);
    const spacingZ = section ? parseFloat(section.spacing) : 3;
    zPositions.push(zPositions[j] + spacingZ);
  }

  // Validar que heightMatrix sea un array, si no, asignar alturas por defecto
  if (!Array.isArray(heightMatrix) || heightMatrix.length === 0) 
  {
    heightMatrix = [{ height: "3" }]; // Si está vacío, asigna un solo piso de altura 3
  }

  // Acumular alturas desde heightMatrix de forma acumulativa
  for (let k = 0; k < heightMatrix.length; k++) 
  {
    const prevHeight = yPositions[k]; // Última altura acumulada
    const height = parseFloat(heightMatrix[k]?.height) || 3; // Altura del piso actual
    yPositions.push(prevHeight + height); // Sumar a la altura acumulada
  }

  // Crear nodos con altura
  for (let k = 0; k < yPositions.length; k++) 
  {
    for (let j = 0; j < zAxis; j++) {
      for (let i = 0; i < xAxis; i++) {
        nodes.push({
          id: `${String.fromCharCode(65 + j)}${i + 1}`, // Solo A1, A2, etc.
          x: parseFloat(xPositions[i].toFixed(2)),
          z: parseFloat(zPositions[j].toFixed(2)),
          y: parseFloat(yPositions[k].toFixed(2)),
          f: k // Piso en un campo separado
        });
      }
    }
  }

  return nodes;
};

// Calcular el pivot en el centro del grid
export const calculatePivot = (points, floorsHeight, pivotPoint) =>
{

  const prePivot = pivotPoint;
  if (points.length === 0) return { x: 0, y: 0, z: 0 };

  const minX = Math.min(...points.map((p) => p.x));
  const maxX = Math.max(...points.map((p) => p.x));
  const minZ = Math.min(...points.map((p) => p.z));
  const maxZ = Math.max(...points.map((p) => p.z));

  const x = (minX + maxX) / 2;
  const y = (floorsHeight) / 2;
  const z = (minZ + maxZ) / 2;

  if(isNaN(x) || isNaN(y) || isNaN(z))
  {
    return {
      x: prePivot.x,
      y: prePivot.y,
      z: prePivot.z
    };
  }
  else
  {
    return {
      x: x,
      y: y,
      z: z
    };
  } 
};

// Crea la matriz de columnas de Hormigón
export const createColumnsMatrix = (gridPoints, heightMatrix) => 
{
  const columns = [];
  let columnsVolume = 0;
  let base = 0;

  for (let floor = 0; floor < heightMatrix.length; floor++) 
  {
    const height = parseFloat(heightMatrix[floor].height);

    // Definir variables locales para cada iteración
    let currentBase = base;
    let currentVolume = columnsVolume;

    gridPoints.forEach(({ id, x, z }) => 
    {
      const b = 0.30; // m
      const h = 0.30; // m
      const area = b * h * 10000; // cm²
      
      const { volume, weight } = calculateVolumeAndWeight(area, height);

      // Actualizar volumen actual
      currentVolume = currentVolume + volume;

      columns.push(
      {
        id,
        section: id,
        activated: true,
        rotated:false,
        position:
        {
          base: currentBase,
          top: currentBase + height,
          floor,
          x,
          z, 
        },
        characteristics:
        {
          material: "concrete",
          type: "210"
        },
        geometry:
        {
          area,
          height: height,
          volume,
          weight,
          b,
          h
        },
        boundaries:
        {
          po:"fixed",
          pf:"fixed"
        }
      });
    });

    // Actualizar fuera del forEach
    base = base + height;
    columnsVolume = currentVolume;
  }

  return columns;
};  

// Crea la matriz de vigas
export const createBeamsMatrix = (gridPoints, heightMatrix, columnsMatrix) => 
{
  if (columnsMatrix.length === 0) return [];

  let beamVolume = 0;
  const beams = [];

  if (!gridPoints || gridPoints.length === 0) 
  {
    return [];
  }

  if (!heightMatrix || heightMatrix.length === 0) 
  {
    return [];
  }

  // Obtener valores únicos de X y Z
  const uniqueX = [...new Set(gridPoints.map(p => p.z))].sort((a, b) => a - b);
  const uniqueZ = [...new Set(gridPoints.map(p => p.x))].sort((a, b) => a - b);

  // Vigas EJE X  
  uniqueX.forEach(z => 
  {
    const pointsInRow = gridPoints
      .filter(p => p.z === z)
      .sort((a, b) => a.x - b.x);

    let prevH = 0

    for (let floor = 0; floor < heightMatrix.length; floor++) 
    {
      prevH = prevH + heightMatrix[floor].height;
      const floorHeight = parseFloat(prevH); 

      for (let i = 0; i < pointsInRow.length - 1; i++) 
      {
        const currentPoint = pointsInRow[i];
        const nextPoint = pointsInRow[i + 1];
        
        const x1 = parseFloat(Math.round(currentPoint.x * 100) / 100);
        const x2 = parseFloat(Math.round(nextPoint.x * 100) / 100);
        const delta = parseFloat(Math.round((x2 - x1) * 100) / 100);

        const columno = Object.values(columnsMatrix).find(item => item.id === currentPoint.id);
        const columnf = Object.values(columnsMatrix).find(item => item.id === nextPoint.id);

        const columno_b = columno.rotated ? columno.geometry.h : columno.geometry.b
        const columno_f = columnf.rotated ? columnf.geometry.h : columnf.geometry.b

        const length = delta - (columno_b/2 + columno_f/2);        
       
        const b = 0.25;
        const h = 0.30;
        const area = b * h * 10000; // cm²
        const { volume, weight } = calculateVolumeAndWeight(area, length);       
        const po = [x1, floorHeight - h / 2, z];
        const pf = [x2, floorHeight - h / 2, z];

        beamVolume  = beamVolume + volume;

        beams.push(
        {
          id: `${currentPoint.id}-${nextPoint.id}`, 
          activated: true,
          position:
          {
            floor: floor + 1, 
            start: [x1,z], 
            end: [x2,z], 
            po,
            pf,          
            direction: 'x',
            co: currentPoint.id,  // Columna inicial
            cf: nextPoint.id,      // Columna final            
          },
          characteristics:
          {
            material: "concrete",
            type: "210"
          },
          geometry:
          {
            area,
            height: floorHeight,
            volume,
            weight,
            length,
            b,
            h,
            As : 7.60, //cm2 cuadrados
            padding : 2, //en centímetros,
            barDiameter : 22, //en milímetros
            numBars : 2
          },
        });
      }
    }
  });
 
  // Vigas EJE Z
  uniqueZ.forEach(x => 
  {
    const pointsInRow = gridPoints
      .filter(p => p.x === x)
      .sort((a, b) => a.z - b.z);

    let prevH = 0;

    for (let floor = 0; floor < heightMatrix.length; floor++) 
    {
      prevH = prevH + heightMatrix[floor].height;
      const floorHeight = parseFloat(prevH); 

      for (let i = 0; i < pointsInRow.length - 1; i++) 
      {          
        const currentPoint = pointsInRow[i];
        const nextPoint = pointsInRow[i + 1];
        
        const z1 = parseFloat(Math.round(currentPoint.z * 100) / 100);
        const z2 = parseFloat(Math.round(nextPoint.z * 100) / 100);
        const delta = parseFloat(Math.round((z2 - z1) * 100) / 100);          

        const columno = Object.values(columnsMatrix).find(item => item.id === currentPoint.id);
        const columnf = Object.values(columnsMatrix).find(item => item.id === nextPoint.id);          

        const columno_b = columno.rotated ? columno.geometry.b : columno.geometry.h;
        const columno_f = columnf.rotated ? columnf.geometry.b : columnf.geometry.h;

        const length = Number((delta - (columno_b/2 + columno_f/2)).toFixed(2));        
        
        const b = 0.25;
        const h = 0.30;
        const area = b * h * 10000; // cm²
        const { volume, weight } = calculateVolumeAndWeight(area, length);
        const po = [x, floorHeight - h / 2, z1]; 
        const pf = [x, floorHeight - h / 2, z2];

        beams.push(
        {
          id: `${currentPoint.id}-${nextPoint.id}`, 
          activated: true,
          position:
          {
            floor: floor + 1, 
            start: [z1,x], 
            end: [z2,x], 
            po,
            pf,          
            direction: 'z',
            co: currentPoint.id,  // Columna inicial
            cf: nextPoint.id      // Columna final            
          },
          characteristics:
          {
            material: "concrete",
            type: "210"
          },
          geometry:
          {
            length,
            area,
            height: floorHeight,
            volume,
            weight,
            b,
            h,
            As : 7.60, //cm2 cuadrados
            padding : 2, //en centímetros,
            barDiameter : 22, //en milímetros
            numBars : 2
          },
        });
      }
    }
  });
  
  return beams;
};

// Crea la matriz de losas
export const createSlabsMatrix = (xAxis, zAxis, gridPoints, heightMatrix, beamsMatrix, selectedAxes, selectedFloor) => 
{
  //console.time('slabtime')
  if (gridPoints.length === 0 || heightMatrix.length === 0) return [];

  let y = 0;
  const panels = [];
  const thickness = 0.15; // Espesor de la losa (15 cm)
  const density = 2.4; // Densidad del concreto (kg/m³)
 
  // Recorrer cada nivel de heightMatrix (desde el piso 1)
  for (let floor = 0; floor < heightMatrix.length; floor++) 
  {
    const height = heightMatrix[floor];
    y = y + height.height;   

    if(floor + 1 !== selectedFloor)
    {
      break;
    }

    // Recorrer en X y Z
    for (let i = 0; i < xAxis - 1; i++) 
    {      
      for (let j = 1; j < zAxis; j++) 
      {        
        //El código solo va a calcular las losas q son afectadas 
        /*
        if(selectedAxes[2] === "X")
        {
          if(j !== selectedAxes[0])
          {
            continue;
          }
        }
        else
        {
          if(getLetter(i) !== selectedAxes[0])
          {
            break;
          }
        }
          */

        // Obtener las esquinas del panel
        const P1 = `${getLetter(i)}${j}`;
        const P2 = `${getLetter(i + 1)}${j}`;
        const P3 = `${getLetter(i)}${j + 1}`;
        const P4 = `${getLetter(i + 1)}${j + 1}`;       
        
        const beamsIDs = 
        [
          `${P1}-${P2}`, // Viga superior
          `${P1}-${P3}`, // Viga izquierda
          `${P2}-${P4}`, // Viga derecha
          `${P3}-${P4}`  // Viga inferior
        ];

        // Buscar las columnas en gridPoints
        const C1 = Object.values(gridPoints).find(item => item.id === P1);
        const C2 = Object.values(gridPoints).find(item => item.id === P2);
        const C3 = Object.values(gridPoints).find(item => item.id === P3);
        const C4 = Object.values(gridPoints).find(item => item.id === P4);
        // Validar que todos los puntos existan
        if (!C1 || !C2 || !C3 || !C4) 
        {
          console.warn(`Puntos indeterminados para el panel: ${P1}, ${P2}, ${P3}, ${P4}`);
          continue; // Saltar al siguiente panel si falta un punto
        }
       
        // Buscar las Vigas en beamsMatrix        
        let B1 = Object.values(beamsMatrix).find(item => item.id === beamsIDs[0] & item.position.floor === floor + 1); //Viga en X
        let B2 = Object.values(beamsMatrix).find(item => item.id === beamsIDs[1] & item.position.floor === floor + 1); //Viga en Z
        let B3 = Object.values(beamsMatrix).find(item => item.id === beamsIDs[2] & item.position.floor === floor + 1); //Viga en Z
        let B4 = Object.values(beamsMatrix).find(item => item.id === beamsIDs[3] & item.position.floor === floor + 1); //Viga en X

        const lengthX = Number(C2.x - C1.x - B1.geometry.b/2 - B4.geometry.b/2).toFixed(2);
        const lengthZ = Number(C3.z - C1.z - B2.geometry.b/2 - B3.geometry.b/2).toFixed(2);    
      
        const distribution = calculateAreaDistribution(lengthX, lengthZ);
        const area = distribution.totalArea;
        const xArea = distribution.xArea;
        const zArea = distribution.zArea;

        // Calcular volumen y peso
        const volume = Number((area * thickness).toFixed(2));
        const weight = Number((volume * density).toFixed(2));  

        const loads =
        { 
          CV: 200, // Carga Viva - kg/cm²
          CM: 350, // Carga Muerta - kg/cm²
          CG: 100, // Carga Granizo - kg/cm²
          CC: 200, // Carga Ceniza - kg/cm²
        }

        // Calcular el centro de la losa en el nivel actual
        const center = 
        {
          x: (Number(((C1.x + C4.x)/2).toFixed(4))), 
          y: y, // Asignar la altura según heightMatrix
          z: (Number(((C1.z + C4.z)/2).toFixed(4)))
        };

        const slab = 
        {
          id: `${P1}-${P2}-${P3}-${P4}-L${floor+1}`, // ID con nivel
          columns: [C1, C2, C3, C4],
          beams: [B1, B2, B3, B4],          

          characteristics:
          {
            material: "concrete",
            type: "210"
          },

          position:
          {
            floor: floor + 1,
            center
          },

          geometry:
          {
            thickness,
            area,
            volume,
            weight,
            lengthX,
            lengthZ,
            xArea,
            zArea,
          },
         
          loads,
        };

        panels.push(slab);
      }
    }
  }
  //console.timeEnd('slabtime')
  return panels;
};  

export const structuralCalculations = (slabsMatrix) => 
{
  const beams = [];
  const slabs = [];

  slabsMatrix.slice().reverse().forEach(slab => 
  {
    const { geometry, loads } = slab;
    const { xArea, zArea, weight, area, lengthX, lengthZ } = geometry;
    const { CM, CV } = loads;

    const SlabCalculatedMoment = calculateSlabUltimateMoment(slab);
    const SlabSectionCalculatedMoment = calculateSlabSectionUltimateMoment(geometry.thickness  - 0.03)

    const MuXLoads = calculateBeamUltimateMoment(
      lengthX,
      (1000 * (weight / area) * xArea) / lengthX,
      (CM * xArea) / lengthX,
      (CV * xArea) / lengthX
    );    

    const MuZLoads = calculateBeamUltimateMoment(
      lengthZ,
      (1000 * (weight / area) * xArea) / lengthX,
      (CM * zArea) / lengthZ,
      (CV * zArea) / lengthZ
    );

    slab.beams.forEach(beam => 
    {
      const { geometry, position } = beam;
      const { direction } = position;

      const linearWeight  = geometry.weight * 1000/ geometry.length;

      let MuAcc = direction === 'x' ? MuXLoads  : MuZLoads;
      
      const MuXLoads_OnlyBeam = calculateBeamUltimateMoment(
        lengthX,
        linearWeight,
        0,
        0
      );    
  
      const MuZLoads_OnlyBeam = calculateBeamUltimateMoment(
        lengthZ,
        linearWeight,
        0,
        0
      );

      let MuAcc_OnlyBeam = direction === 'x' ? MuXLoads_OnlyBeam  : MuZLoads_OnlyBeam;
      //let MuAcc_WithBeam = direction === 'x' ? MuXLoads + MuXLoads_OnlyBeam : MuZLoads + MuZLoads_OnlyBeam;      
     
      // Verificar si la viga ya existe
      const index = beams.findIndex(b => b.id === beam.id && b.floor === beam.position.floor);

      let MuSlabLoads = MuAcc;
      // Si ya existe, sumamos el MuAcc actual al MuLoads existente
      if (index !== -1) 
      {
        MuAcc += beams[index].MuLoads;
        MuSlabLoads += beams[index].MuSlabLoads;
        
        beams[index].MuLoads = MuAcc;
        beams[index].MuSlabLoads = MuSlabLoads;
      } 
      else 
      {
        // Si no existe, la añadimos
        MuAcc += MuAcc_OnlyBeam;
        beams.push({
          id: beam.id,
          floor: beam.position.floor,
          MuLoads: MuAcc,
          MuSlabLoads : MuSlabLoads,
          pass: null,  // Inicialmente null, se actualizará después
          linearWeight:Number(geometry.weight * 1000/ geometry.length).toFixed(2),
        });
      }

      // Calcular la resistencia de la sección
      const { b, h, padding, barDiameter, As } = geometry;
      const d = h - padding/100 - barDiameter/2000;

      const MuSection = calculateBeamSectionUltimateMoment(b, d, As);

      // Actualizar el campo 'pass' según el resultado del análisis estructural
      const passes = MuSection > MuAcc;

      // Actualizar el objeto beam con el resultado del análisis
      const updatedBeam = 
      {
        id: beam.id,
        MuLoads: MuAcc,
        MuSlabLoads: MuSlabLoads,
        floor: beam.position.floor,
        pass: passes,
        linearWeight:Number(geometry.weight * 1000/ geometry.length).toFixed(2),
      };      

      // Reemplazar la viga existente con el objeto actualizado
      const updateIndex = beams.findIndex(b => b.id === beam.id && b.floor === beam.position.floor);
      beams[updateIndex] = updatedBeam;
    });   

    slabs.push({
      id: slab.id,
      MuCalculated: SlabCalculatedMoment,
      pass: SlabSectionCalculatedMoment > SlabCalculatedMoment.MuMax ? true : false,
      floor:slab.position.floor,
      loads:
      {
        LoadsX:
        {
          PP : (1000 * (weight / area) * xArea) / lengthX,
          CM : (CM * xArea) / lengthX,
          CV: (CV * xArea) / lengthX
        },

        LoadsZ:
        {
          PP : (1000 * (weight / area) * zArea) / lengthZ,
          CM : (CM * zArea) / lengthZ,
          CV: (CV * zArea) / lengthZ
        },
      }
    });
  });
  

  const structural   = 
  {
    beamsStructural : beams,
    slabsStructural : slabs
  }
  
  return structural;
};  
  
// Calcula la matriz de reacciones debido al peso propio de las columnas y vigas
export const calculateTotalVolume = (columnsMatrix, beamsMatrix, slabsMatrix) => 
{
  let columnsVolume = 0;
  let beamsVolume = 0;
  let slabsVolume = 0;

  columnsMatrix.forEach(({ geometry }) => 
  {
    const {volume} = geometry;
    columnsVolume = columnsVolume + volume;
  });

  beamsMatrix.forEach(({ geometry }) => 
  {
    const {volume} = geometry;
    beamsVolume = beamsVolume + volume;
  });

  slabsMatrix.forEach(({ geometry }) => 
  {
    const {volume} = geometry;
    slabsVolume = slabsVolume + volume;
  });

  return(Number(columnsVolume + beamsVolume + slabsVolume).toFixed(2));
};

export const calculateTotalWeight = (columnsMatrix, beamsMatrix, slabsMatrix) => 
{
  let columnsWeight = 0;
  let beamsWeight = 0;
  let slabsWeight = 0;

  columnsMatrix.forEach(({ geometry }) => 
  {
    const {weight} = geometry;
    columnsWeight = columnsWeight + weight;
  });

  beamsMatrix.forEach(({ geometry }) => 
  {
    const {weight} = geometry;
    beamsWeight = beamsWeight + weight;
  });

  slabsMatrix.forEach(({ geometry }) => 
  {
    const {weight} = geometry;
    slabsWeight = slabsWeight + weight;
  });

  return(Number(columnsWeight + beamsWeight + slabsWeight).toFixed(2));
};

// Calcula la matriz de reacciones debido al peso propio de las columnas y vigas
export const createReactionsMatrix = (columnsMatrix, beamsMatrix) => 
{
  const reactions = {};

  columnsMatrix.forEach(({ id, geometry }) => 
  {
    if (!reactions[id]) 
    {
      reactions[id] = 
      { 
        id, 
        reaction: 
        {
          P: {
              PP: 0, // Inicializar en 0
              CV: 0,
              CM: 0,
              CG: 0,
              CC: 0
          },
          Mx: {
              PP: 0,
              CV: 0,
              CM: 0,
              CG: 0,
              CC: 0
          },
          Mz: {
            PP: 0,
            CV: 0,
            CM: 0,
            CG: 0,
            CC: 0
          }
        }
      };
    }

    // Sumar el peso al PP existente
    reactions[id].reaction.P.PP += geometry.weight;

    beamsMatrix.forEach(({ position, geometry }) => 
    {
      const { co, cf, floor, direction, pf, start, end} = position;
      const { weight, length } = geometry;      
      
      const fixedEndMoment = weight * length / 12; // Momento de empotramiento perfecto (wL²/12)  
     
      if ((id === co) && floor !== 0 && direction === 'z') 
      {
        reactions[id].reaction.Mx.PP -= fixedEndMoment;
      }

      if ((id === cf) && floor !== 0 && direction === 'z') 
      {
        reactions[id].reaction.Mx.PP += fixedEndMoment;
      }

      if ((id === co) && floor !== 0 && direction === 'x') 
      {
        reactions[id].reaction.Mz.PP += fixedEndMoment;
      }

      if ((id === cf) && floor !== 0 && direction === 'x') 
      {
        reactions[id].reaction.Mz.PP -= fixedEndMoment;
      }
    });
    
  });

  console.log(reactions);
  return Object.values(reactions);
};

export const calculateAreaDistribution = (lengthX, lengthZ) => 
{
  // Convertir a números por si se pasan como cadenas de texto
  const Lx = parseFloat(lengthX);
  const Lz = parseFloat(lengthZ);  

  let xArea;
  let zArea;

  const Lmax = Math.max(Lx, Lz);

  // Verificar si los valores son válidos
  if (isNaN(Lx) || isNaN(Lz) || Lx <= 0 || Lz <= 0) 
  {
    //throw new Error("Las longitudes deben ser números positivos.");
  }

  if(Lx === Lmax)
  {
    //trapecio
    xArea = areaTrapecio(Lx , Lx - Lz, Lz/2);
  }
  else
  {
    //triángulo
    xArea = areaTriangulo(Lx , Lx/2);
  }

  if(Lz === Lmax)
  {
    //trapecio
    zArea = areaTrapecio(Lz , Lz - Lx, Lx/2);
  }
  else
  {
    //triángulo
    zArea = areaTriangulo(Lz , Lz/2);
  }

  const totalArea = areaRectangulo(Lx , Lz);
  

  return {
    xArea: xArea,
    zArea: zArea,
    totalArea : totalArea
  };
};

export const areaTriangulo = (base, altura) => 
{
  // Verificar si los valores son válidos
  if (base <= 0 || altura <= 0) 
  {
    //throw new Error("La base y la altura deben ser números positivos.");
  }
 
  return Number((0.5 * base * altura).toFixed(4));
};

export const areaTrapecio = (baseMayor, baseMenor, altura) => 
{
  // Verificar si los valores son válidos
  if (baseMayor <= 0 || baseMenor <= 0 || altura <= 0) 
  {
    //throw new Error("Todas las dimensiones deben ser números positivos.");
  }

  return Number((0.5 * (baseMayor + baseMenor) * altura).toFixed(4));
};

export const areaRectangulo = (base, altura) => 
{
  // Verificar si los valores son válidos
  if (base <= 0 || altura <= 0) 
  {
    //throw new Error("La base y la altura deben ser números positivos.");
  }

  // Calcular el área y formatear a 4 decimales
  return Number((base * altura).toFixed(4));
};

export const calculateBeamSectionUltimateMoment = ( b, d, As ) => 
{
  //b en metros
  //d en metros 
  //As en cm²

  const beta1 = 0.85;   // Para concretos comunes
  const phi = 0.9;      // Factor de reducción de resistencia
  const fc = 210;       // Resistencia del concreto (kg/cm²)
  const fy = 4200;      // Esfuerzo de fluencia del acero (kg/cm²)

  // Convertir de metros a centímetros
  const bCm = b * 100;
  const dCm = d * 100;  

  // 2. Calcular a y c para Mu
  const c = (As * fy) / (0.85 * fc * bCm);
  const a = beta1 * c;

  // 3. Calcular el Momento Último
  const Mu = phi * As * fy * (dCm - (a / 2)); // kg·cm

  // Convertir de kg·cm a kg·m
  const MuKgM = Mu / 100;

  return Number(MuKgM.toFixed(4)); // kg·m
};

export const calculateBeamUltimateMoment = (longitud, pp, cm, cv) => 
{
  // Cálculo de la carga distribuida total
  const qu = 1.4 * pp + 1.4 * cm + 1.7 * cv;

  // Cálculo del Momento Último Factorado Flexión en el Centro
  let MuCentro = (qu * Math.pow(longitud, 2)) / 8; // MOMENTO EN kg - m  
  MuCentro = Number(MuCentro.toFixed(4));

  /*
  // Cálculo del Momento Último Factorado Flexión en el Centro 
  let MuExtremos = (qu * Math.pow(longitud, 2)) / 12; // MOMENTO EN kg - m
  MuExtremos = Number(MuExtremos.toFixed(4));
  
  // Cálculo del Momento Último Factorado Flector para diseño de las columnas
  let MuExtremosFlector = (qu * Math.pow(longitud, 2)) / 2; // MOMENTO EN kg - m
  MuExtremosFlector =  Number(MuExtremosFlector.toFixed(4));

  console.log(MuCentro, MuExtremos, MuExtremosFlector);
  */
  // Retorno con 4 decimales
  return MuCentro;
};

export const calculateSlabUltimateMoment = (slab) => 
{
  const {geometry, loads} = slab;
  const {lengthX, lengthZ, area, weight}  =  geometry;
  const {CM, CV}  =  loads;
  const PP = Number(weight * 1000 / area).toFixed(2);

  const qu = Number((1.4 * PP + 1.4 * CM + 1.7 * CV)/1000).toFixed(2); // kg/m2;

  const Lmin = Math.min(lengthX, lengthZ);
  const Lmax = Math.max(lengthX, lengthZ);
  const ratio = Lmin / Lmax;

  let alpha_min, alpha_max;

  if (ratio <= 1.0) {
    alpha_min = 0.062;
    alpha_max = 0.062;
  } else if (ratio <= 1.5) {
    alpha_min = 0.079;
    alpha_max = 0.049;
  } else if (ratio <= 2.0) {
    alpha_min = 0.086;
    alpha_max = 0.036;
  } else if (ratio <= 3.0) {
    alpha_min = 0.092;
    alpha_max = 0.026;
  } else {
    alpha_min = 0.10; // Aproximación conservadora
    alpha_max = 0.02;
  }  

  const M_Lmin = Number(alpha_min * qu * Lmin ** 2).toFixed(2);
  const M_Lmax = Number(alpha_max * qu * Lmax ** 2).toFixed(2);
  

  return(lengthX < lengthZ
    ? { MuX: M_Lmin, MuZ: M_Lmax, MuMax: Math.max(M_Lmax, M_Lmin) }
    : { MuX: M_Lmax, MuZ: M_Lmin, MuMax: Math.max(M_Lmax, M_Lmin) });  
  
};

export const calculateSlabSectionUltimateMoment = ( d ) => 
{
  const beta1 = 0.85;   // Para concretos comunes
  const phi = 0.9;      // Factor de reducción de resistencia
  const fc = 210;       // Resistencia del concreto (kg/cm²)
  const fy = 4200;      // Esfuerzo de fluencia del acero (kg/cm²)

  // Convertir de metros a centímetros
  const bCm = 1 * 100;
  const dCm = d * 100;

  // 1. Calcular As,min
  const AsMin1 = 0.0018 * bCm * dCm;
  const AsMin2 = (1.4 / fy) * bCm * dCm;
  const AsMin = Math.max(AsMin1, AsMin2);

  const As = Math.max(AsMin, 4.52); // 4.52 cm2 es el área de 4 varillas de diámetro 12 mm en 1 metro d ancho de losa

  // 2. Calcular a y c para Mu
  const c = (As * fy) / (0.85 * fc * bCm);
  const a = beta1 * c;

  // 3. Calcular el Momento Último
  const Mu = phi * As * fy * (dCm - (a / 2)); // kg·cm

  // Convertir de kg·cm a kg·m
  const MuKgM = Mu / 100;

  //console.log(Number(MuKgM.toFixed(4))); 

  return Number((MuKgM/1000).toFixed(4)); // T·m
};

export const createColumnsMatrixMetalic = (gridPoints, heightMatrix) => 
{
  const columns = [];
  let base = 0;

  for (let floor = 0; floor < heightMatrix.length; floor++) {
    const height = parseFloat(heightMatrix[floor].height);

    // Definir variables locales para cada iteración
    let currentBase = base;

    gridPoints.forEach(({ id, x, z }) => 
    {
      const b = 0.10; // m
      const h = 0.10; // m
      const e = 0.003; // m
      const pl = 8.96; // kg/m
      const i = 3.94; // cm
      const A = 11.41; // cm²
      const Sx = 35.42; // cm³
      const I = 177.08; // cm⁴

      const { weight } = calculateWeightMetallic(pl, height);

      // Crear la columna
      const column = {
        id,
        section: id,
        activated: true,
        rotated: false,
        position: {
          base: currentBase,
          top: currentBase + height,
          floor,
          x,
          z,
        },
        characteristics: {
          material: "steel",
          type: "A36",
          fy: 2532, // kg/cm²
          E: 2100000, // kg/cm² (módulo de elasticidad del acero)
          density: 7.85, // Ton/m³
        },
        geometry: {
          height: height,
          weight,
          b,
          h,
          e,
          pl,
          i,
          A,
          Sx,
          I,
        },
        boundaries: {
          po: "fixed",
          pf: "fixed",
        },
      };

      // Calcular Mu y Pu para la columna
      const { ultimateMoment, ultimateAxialLoad } = calculateUltimateLoads(column);

      // Añadir la propiedad "structural" al objeto de la columna
      column.structural = {
        M_ultimate_Tm: ultimateMoment, // Momento último en T·m
        P_ultimate_T: ultimateAxialLoad, // Carga axial última en T
      };

      // Agregar la columna a la matriz
      columns.push(column);
      console.log(column)
    });

    // Actualizar fuera del forEach
    base = base + height;
  }

  return columns;
};

export const updateColumnsMatrixMetalic = (gridPoints, heightMatrix, columnsMatrix) => 
{
  const columns = [];
  let base = 0;

  for (let floor = 0; floor < heightMatrix.length; floor++) 
  {
    const height = parseFloat(heightMatrix[floor].height);

    // Definir variables locales para cada iteración
    let currentBase = base;

    gridPoints.forEach(({ id, x, z }) => 
    {
      const index = columnsMatrix.findIndex((c) => id === c.id && floor === c.position.floor);

      // Tomar las propiedades de la columna existente si está disponible
      const existingColumn = columnsMatrix[index];

      const { 
        b = 0.10,   // m
        h = 0.10,   // m
        e = 0.003,  // m
        pl = 8.96,  // kg/m
        i = 3.94,   // cm
        A = 11.41,  // cm²
        Sx = 35.42, // cm³
        I = 177.08  // cm⁴
      } = existingColumn?.geometry ?? {};

      console.log(b, height, h, e);


      const { weight } = calculateWeightMetallic(pl, height);

      // Crear la columna
      const column = 
      {
        id,
        section: id,
        activated: true,
        rotated: false,
        position: {
          base: currentBase,
          top: currentBase + height,
          floor,
          x,
          z,
        },
        characteristics: {
          material: "steel",
          type: "A36",
        },
        geometry: {
          height: height,
          weight,
          b,
          h,
          e,
          pl,
          i,
          A,
          Sx,
          I,
        },
        boundaries: {
          po: "fixed",
          pf: "fixed",
        },
        structural: {
          M_ultimate_Tm: existingColumn?.structural?.M_ultimate_Tm || null, // Momento último en T·m
          P_ultimate_T: existingColumn?.structural?.P_ultimate_T || null, // Carga axial última en T
        },
      };

      // Agregar la columna a la matriz
      columns.push(column);
    });

    // Actualizar fuera del forEach
    base = base + height;
  }

  return columns;
};

// Calcula el volumen y el peso de un elemento estructural
export const calculateWeightMetallic = (pl, length) => 
{  
  const weight = parseFloat(Number(pl * length).toFixed(4)); // Toneladas
  return { weight };
};

const calculateUltimateLoads = (column) => 
{
  const { b, h, I, A, height } = column.geometry; // b, h en metros (m), I en cm⁴, A en cm², height en metros (m)
  const { fy, E } = column.characteristics; // fy en kg/cm², E en kg/cm²

  // Convertir unidades (de cm a m)
  const I_m4 = I * 1e-8; // Momento de inercia: de cm⁴ a m⁴ (1 cm⁴ = 1e-8 m⁴)
  const A_m2 = A * 1e-4; // Área: de cm² a m² (1 cm² = 1e-4 m²)

  // 1. Momento último en flexión (M_u) en toneladas-metro (T·m)
  const Z = (b * h ** 2) / 4; // Módulo de sección plástico (m³)
  const M_u_kgcm = Z * 1e6 * fy; // Convertir Z a cm³ y multiplicar por fy (kg/cm²)
  const M_u_Tm = M_u_kgcm / 100000; // Convertir kg·cm a T·m

  // 2. Carga axial última en compresión (P_u) en toneladas (T)
  const L_cm = height * 100; // Longitud de la columna en cm
  const r_cm = Math.sqrt(I / A); // Radio de giro en cm
  const P_cr_kg = (Math.PI ** 2 * E * A) / ((0.5 * L_cm / r_cm) ** 2); // Carga crítica en kg
  const P_u_kg = A * fy; // Carga axial última en kg
  const P_ultimate_kg = Math.min(P_cr_kg, P_u_kg); // Valor crítico en kg
  const P_ultimate_T = P_ultimate_kg / 1000; // Convertir kg a T

  console.log("Momento último (Mu):", M_u_Tm.toFixed(2), "T·m");
  console.log("Carga axial última (Pu):", P_ultimate_T.toFixed(2), "T");

  return {
    ...column,
    ultimateMoment: M_u_Tm.toFixed(2), // Momento último en T·m
    ultimateAxialLoad: P_ultimate_T.toFixed(2), // Carga axial última en T
  };
};